import { Link as CarbonLink, Tile } from '@carbon/react';
import { Launch } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ListTable, type ListColumn } from '../components/ListTable';
import { PageHeader } from '../components/PageHeader';
import { StatusTag } from '../components/StatusTag';
import { FilterLayout } from '../filters/FilterPanel';
import { useFilters } from '../filters/useFilters';
import { asProblem, problemMessage } from '../problems';
import {
  DELIVERY_STATUSES,
  dateRange,
  ordersKey,
  today,
  useFormat,
  useOrdersApi,
  type OrdersMode,
} from './model';
import { DateStoreFilters, FilterDropdown, StoreName, type DateStoreValue } from './TransactionFilters';
import './orders.scss';

const PAGE_SIZES = [25, 50, 100];
const OWN_DRIVER = 'own_driver';
const STATUSES = DELIVERY_STATUSES;

/** Every delivery across orders by provider, or by the store's own drivers (FR-INT-05, ADR 0010). */
export function ShippingTransactionsPage({ mode }: { mode: OrdersMode }) {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const navigate = useNavigate();
  const format = useFormat();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const filters = useFilters({ from: today(), to: today(), storeId: '', provider: '', status: '' }, () => setPage(1));
  const { provider, status } = filters.applied;
  const range: DateStoreValue = {
    from: filters.applied.from,
    to: filters.applied.to,
    storeId: filters.applied.storeId,
  };
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const providers = useQuery({
    queryKey: [...ordersKey, 'shipping-providers'],
    queryFn: () => api.shippingProviders(),
  });

  const query = {
    ...dateRange(range.from, range.to),
    storeId: range.storeId || undefined,
    provider: provider || undefined,
    status: status || undefined,
    search: search || undefined,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: [...ordersKey, 'shipping-transactions', query],
    queryFn: () => api.shippingTransactions(query),
    placeholderData: keepPreviousData,
  });
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };
  const statusLabel = (s: string) => t(`orders.delivery.statuses.${s}` as I18nKey, { defaultValue: s });

  type Item = NonNullable<typeof list.data>['items'][number];
  const providerName = (d: Item) =>
    d.method === OWN_DRIVER ? t('transactions.filters.ownDriver') : (d.providerName ?? d.providerCode ?? '');
  const columns: ListColumn<Item>[] = [
    {
      key: 'createdAt',
      header: t('transactions.shipping.columns.createdAt'),
      sortable: true,
      render: (d) => format.dateTime(d.createdAt),
    },
    {
      key: 'order',
      header: t('transactions.shipping.columns.order'),
      render: (d) => <Link to={`/orders/${d.orderId}`}>{d.orderNumber}</Link>,
    },
    ...(mode === 'ho'
      ? [
          {
            key: 'store',
            header: t('transactions.shipping.columns.store'),
            render: (d: Item) => <StoreName id={d.storeId} />,
          },
        ]
      : []),
    { key: 'provider', header: t('transactions.shipping.columns.provider'), render: providerName },
    {
      key: 'reference',
      header: t('transactions.shipping.columns.reference'),
      render: (d) => (
        <>
          {d.providerReference ?? ''}
          {d.trackingUrl && (
            <CarbonLink
              href={d.trackingUrl}
              target="_blank"
              rel="noreferrer"
              renderIcon={Launch}
              className="pos-transactions__track"
            >
              {t('orders.delivery.track')}
            </CarbonLink>
          )}
        </>
      ),
    },
    {
      key: 'driver',
      header: t('transactions.shipping.columns.driver'),
      render: (d) => [d.driverName, d.driverPhone].filter(Boolean).join(' · '),
    },
    {
      key: 'customer',
      header: t('transactions.shipping.columns.customer'),
      render: (d) => [d.customerName, d.customerPhone].filter(Boolean).join(' · '),
    },
    {
      key: 'fee',
      header: t('transactions.shipping.columns.fee'),
      sortable: true,
      sortValue: (d) => Number(d.fee),
      className: 'pos-admin-page__number',
      render: (d) => format.money(d.fee, d.currencyCode),
    },
    {
      key: 'status',
      header: t('transactions.shipping.columns.status'),
      render: (d) => (
        <>
          <StatusTag status={d.status}>{statusLabel(d.status)}</StatusTag>
          {d.failureReason && <p className="pos-orders__muted">{d.failureReason}</p>}
        </>
      ),
    },
    {
      key: 'deliveredAt',
      header: t('transactions.shipping.columns.deliveredAt'),
      render: (d) => format.dateTime(d.deliveredAt),
    },
  ];
  const items = list.data?.items ?? [];

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('transactions.shipping.title')} />
      <FilterLayout
        filters={filters}
        fields={
          <>
            <DateStoreFilters
              idPrefix="shipping"
              mode={mode}
              value={{ from: filters.draft.from, to: filters.draft.to, storeId: filters.draft.storeId }}
              onChange={(v) => filters.setDraft((d) => ({ ...d, ...v }))}
            />
            <FilterDropdown
              id="shipping-provider"
              label={t('transactions.filters.provider')}
              options={[
                { id: OWN_DRIVER, label: t('transactions.filters.ownDriver') },
                ...(providers.data ?? []).map((p) => ({ id: p.code, label: p.name })),
              ]}
              value={filters.draft.provider}
              onChange={(v) => filters.set('provider', v)}
            />
            <FilterDropdown
              id="shipping-status"
              label={t('transactions.filters.status')}
              options={[
                { id: 'active', label: t('transactions.filters.active') },
                ...STATUSES.map((s) => ({ id: s, label: statusLabel(s) })),
              ]}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
          </>
        }
      >
        {(list.data?.summary.length ?? 0) > 0 && (
          <div className="pos-transactions__summary">
            {list.data?.summary.map((s) => (
              <Tile key={`${s.method}-${s.providerCode ?? ''}-${s.currencyCode}`} className="pos-transactions__tile">
                <p className="pos-transactions__tile-title">
                  {s.method === OWN_DRIVER
                    ? t('transactions.filters.ownDriver')
                    : (s.providerName ?? s.providerCode ?? '')}
                </p>
                <p className="pos-orders__muted">
                  {t('transactions.shipping.summary.count', { count: Number(s.count) })}
                </p>
                <dl className="pos-transactions__tile-figures">
                  <dt>{t('transactions.shipping.summary.active')}</dt>
                  <dd className="pos-admin-page__number">{format.number(s.active)}</dd>
                  <dt>{t('transactions.shipping.summary.delivered')}</dt>
                  <dd className="pos-admin-page__number">{format.number(s.delivered)}</dd>
                  {Number(s.failed) > 0 && (
                    <>
                      <dt>{t('transactions.shipping.summary.failed')}</dt>
                      <dd className="pos-admin-page__number">{format.number(s.failed)}</dd>
                    </>
                  )}
                  <dt>{t('transactions.shipping.summary.fees')}</dt>
                  <dd className="pos-admin-page__number">{format.money(s.fees, s.currencyCode)}</dd>
                </dl>
              </Tile>
            ))}
          </div>
        )}

        <ListTable
          ariaLabel={t('transactions.shipping.title')}
          columns={columns}
          rows={items}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          search={{ label: t('transactions.shipping.search'), onChange: reset(setSearch) }}
          filters={filters}
          onRowClick={(d) => void navigate(`/sales/shipping/${d.id}`)}
          empty={{ heading: t('transactions.shipping.empty') }}
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            onChange: ({ page: next, pageSize: size }) => {
              setPage(next);
              setPageSize(size);
            },
          }}
        />
      </FilterLayout>
    </section>
  );
}
